import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle, Zap } from 'lucide-react';
import { 
  parseSMARTLaunchParams, 
  initiateSMARTLaunch, 
  handleSMARTCallback,
  getSMARTLaunchContext,
} from '@/lib/epic/smartLauncher';
import { fetchFullContext } from '@/lib/epic/fhirClient';
import linkLogo from '@/assets/link-logo-tight.png';

const EPIC_CLIENT_ID = import.meta.env.VITE_EPIC_CLIENT_ID || 'link-auth-app';
const EPIC_REDIRECT_URI = `${window.location.origin}/launch`;

type LaunchState = 'initializing' | 'authorizing' | 'loading_context' | 'success' | 'error';

export default function SMARTLaunch() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [launchState, setLaunchState] = useState<LaunchState>('initializing');
  const [error, setError] = useState<string | null>(null);
  const [contextInfo, setContextInfo] = useState<{
    patientName?: string;
    payerName?: string;
    diagnosisCount?: number;
  }>({});
  
  useEffect(() => {
    async function handleLaunch() {
      const params = parseSMARTLaunchParams(searchParams);
      
      // Case 1: OAuth callback (has code and state)
      if (params.code && params.state) {
        setLaunchState('authorizing');
        
        try {
          const tokens = await handleSMARTCallback(
            params.code,
            params.state,
            EPIC_CLIENT_ID,
            EPIC_REDIRECT_URI
          );
          
          // Load patient context
          setLaunchState('loading_context');
          
          if (tokens.patient) {
            const context = await fetchFullContext(
              tokens.patient,
              EPIC_CLIENT_ID,
              tokens.encounter
            );
            
            setContextInfo({
              patientName: context.patient 
                ? `${context.patient.firstName} ${context.patient.lastName}`
                : undefined,
              payerName: context.coverage?.payerName,
              diagnosisCount: context.diagnoses?.length || 0,
            });
            
            // Store context for wizard to pick up
            sessionStorage.setItem('fhir_context', JSON.stringify({
              patient: context.patient,
              coverage: context.coverage,
              diagnoses: context.diagnoses,
              launchContext: {
                patientId: tokens.patient,
                encounterId: tokens.encounter,
                practitionerId: tokens.fhirUser,
                launchedFromEHR: true,
              },
            }));
          }
          
          setLaunchState('success');
          
          // Auto-redirect after brief delay
          setTimeout(() => {
            navigate('/surgeon', { replace: true });
          }, 2000);
          
        } catch (err) {
          console.error('OAuth callback failed:', err);
          setError(err instanceof Error ? err.message : 'Authorization failed');
          setLaunchState('error');
        }
        
        return;
      }
      
      // Case 2: EHR launch (has iss and launch)
      if (params.iss && params.launch) {
        setLaunchState('authorizing');
        
        try {
          // This will redirect to Epic authorization
          await initiateSMARTLaunch(
            params.iss,
            params.launch,
            EPIC_CLIENT_ID,
            EPIC_REDIRECT_URI
          );
          // Won't reach here - redirect happens
        } catch (err) {
          console.error('SMART launch failed:', err);
          setError(err instanceof Error ? err.message : 'Launch failed');
          setLaunchState('error');
        }
        
        return;
      }
      
      // Case 3: No valid parameters
      setError('Missing launch parameters. This page should be accessed from within Epic.');
      setLaunchState('error');
    }
    
    handleLaunch();
  }, [searchParams, navigate]);
  
  const stateMessages: Record<LaunchState, { title: string; description: string }> = {
    initializing: {
      title: 'Initializing...',
      description: 'Preparing SMART on FHIR launch',
    },
    authorizing: {
      title: 'Connecting to Epic',
      description: 'Establishing secure connection with EHR',
    },
    loading_context: {
      title: 'Loading Patient Context',
      description: 'Retrieving patient, coverage, and clinical data',
    },
    success: {
      title: 'Launch Successful',
      description: 'Redirecting to authorization wizard...',
    },
    error: {
      title: 'Launch Failed',
      description: error || 'An error occurred during launch',
    },
  };
  
  const currentMessage = stateMessages[launchState];
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={linkLogo} alt="LINK" className="h-10" />
          </div>
          <CardTitle className="flex items-center justify-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            SMART on FHIR Launch
          </CardTitle>
          <CardDescription>Epic EHR Integration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status */}
          <div className="flex flex-col items-center gap-4 py-6">
            {launchState === 'error' ? (
              <XCircle className="w-12 h-12 text-destructive" />
            ) : launchState === 'success' ? (
              <CheckCircle2 className="w-12 h-12 text-primary" />
            ) : (
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
            )}
            
            <div className="text-center">
              <h3 className="font-semibold">{currentMessage.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {currentMessage.description}
              </p>
            </div>
          </div>
          
          {/* Context info on success */}
          {launchState === 'success' && (
             <div className="space-y-2 p-4 rounded-lg bg-primary/10 border border-primary/30">
               <p className="text-sm font-medium text-primary">Context Retrieved</p>
              <div className="space-y-1 text-sm text-muted-foreground">
                {contextInfo.patientName && (
                  <p>Patient: <span className="text-foreground">{contextInfo.patientName}</span></p>
                )}
                {contextInfo.payerName && (
                  <p>Coverage: <span className="text-foreground">{contextInfo.payerName}</span></p>
                )}
                {contextInfo.diagnosisCount !== undefined && contextInfo.diagnosisCount > 0 && (
                  <p>Diagnoses: <span className="text-foreground">{contextInfo.diagnosisCount} loaded</span></p>
                )}
              </div>
            </div>
          )}
          
          {/* Error retry */}
          {launchState === 'error' && (
            <div className="flex flex-col gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/surgeon')}
              >
                Continue Without Epic Data
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                You can still use LINK without Epic integration
              </p>
            </div>
          )}
          
          {/* SMART badge */}
          <div className="flex justify-center">
            <Badge variant="outline" className="text-xs">
              SMART on FHIR v2.0
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
